import Component from '@glimmer/component';
import Table from 'tio-reimagined/components/table';
import Paginator from 'tio-reimagined/components/paginator';
import { LinkTo } from '@ember/routing';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { eq } from 'tio-ui/utilities';
import { tracked } from '@glimmer/tracking';
import type { ProgramsSearchResult } from 'tio-data/models/search';
import { reorderTableColumns, buildResultKeyLookup, getResultKey } from '../../utils/table-utils';
import { autoGenerateLabel, findAvailableFieldLabel } from '../../utils/label-generation';
import { TAS_PROGRAMS_COLUMN_MAPPING } from '../../utils/tas-column-config';
import type { FieldOption } from '../../utils/opensearch-field-mapper';
import { employeeCustomFieldValue } from '../../utils/employee-custom-field-resolver';
import { tasCustomFieldValue } from '../../utils/tas-custom-field-resolver';

// ============================================================================
// DATA FLOW RULES - READ THIS FIRST!
// ============================================================================
//
// **FIELD NAME FORMATS & NORMALIZATION**
//
// There are TWO formats for field names in this system:
//
// 1. **OPENSEARCH / QUERY PARAMS** (snake_case):
//    - How OpenSearch stores fields internally
//    - What we send in API requests (_source parameter)
//    - What column selector UI displays
//    - Example: employee_name_keyword, tas_application_field_courses_begin_date_date
//
// 2. **API RESPONSE DATA** (camelCase):
//    - What Rails returns after deep_camelize_keys()
//    - What item properties are named
//    - Example: employeeNameKeyword, tasApplicationFieldCoursesBeginDateDate
//
// **NORMALIZATION STRATEGY**:
// The /fields API provides a `resultKey` for each field — the camelCase key
// as it appears in search result documents. We use this server-provided key
// instead of doing frontend case conversion:
//
// 1. **Entry Point** (displayColumns getter):
//    - selectedColumns arrive as snake_case from query params
//    - Look up resultKey via buildResultKeyLookup(availableFields)
//    - Store resultKey in column.key for internal use
//
// 2. **Internal Processing** (camelCase only):
//    - Labels come from API displayLabel (via findAvailableFieldLabel)
//    - getCellValue() receives camelCase only (matches API response)
//    - All column keys in displayColumns are camelCase
//
// 3. **Benefits**:
//    - No frontend case-conversion logic needed
//    - Server is the single source of truth for field→result key mapping
//    - Correctly handles flattened nested fields (e.g., employee_ef_fields)
//
// **LABEL GENERATION**:
// - Labels come from API's displayLabel (already disambiguated)
// - findAvailableFieldLabel() looks up the API-provided label
// - autoGenerateLabel() is the fallback for fields not in /fields response
//
// ============================================================================
// OTHER IMPORTANT RULES
// ============================================================================
//
// 1. **API RETURNS CAMELCASE**: All data from backend comes in camelCase
//    Example: { employeeName: "John", tasProgramInstanceFieldName: "MBA" }
//
// 2. **QUERIES USE SNAKE_CASE**: Filters and column selectors send snake_case
//    Example: employee_name_keyword, tas_program_template_field_program_name_keyword
//
// 3. **UI REFLECTS CURRENT QUERY**: Column selector shows what's being queried
//    - If selectedColumns includes "employee_name_keyword", that box is checked
//    - Default columns should use snake_case keys to match column selector
//
// 4. **COLUMN KEYS FROM MAPPING**: Column keys come from COLUMN_MAPPING object
//    - Keys are snake_case (what UI uses to identify columns)
//    - Values include label, type, and linkable route
//
// 5. **LABELS**: API displayLabel preferred, with COLUMN_MAPPING overrides
//    - findAvailableFieldLabel() looks up API-provided displayLabel
//    - autoGenerateLabel() fallback for fields not in /fields response
//    - COLUMN_MAPPING only needed when label should differ from API label
//    - Example: "tas_program_instance_field_school_institution_name_keyword" → "School"
//
// ============================================================================

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

type ColumnKey = keyof typeof COLUMN_MAPPING;
type ColumnType = 'text' | 'currency' | 'date' | 'boolean' | 'array-primitive' | 'array-objects';

/**
 * Configuration for displaying nested object fields.
 * Used to control which fields are shown from array objects (e.g., courses).
 */
interface NestedFieldConfig {
  /** Field key in the nested object (can be camelCase or snake_case) */
  readonly field: string;
  /** Optional custom label for the field. Use false to show value only without label. If undefined, auto-generates label. */
  readonly label?: string | false;
  /** Optional type override for the field */
  readonly type?: ColumnType;
}

/**
 * Configuration for array-objects column type.
 * Allows specifying which nested fields to display and how to format them.
 */
interface ArrayObjectsConfig {
  /** Fields to display from each nested object */
  readonly fields: readonly NestedFieldConfig[];
  /** Separator between field entries (default: '\n') */
  readonly separator?: string;
}

interface DisplayColumn {
  key: string;
  label: string;
  type: ColumnType;
  linkable: string | false;
  /** Configuration for array-objects type - which nested fields to show */
  arrayConfig?: ArrayObjectsConfig;
}

type RouteLinkTarget = { route: string; modelId: string };
type ExternalLinkTarget = { href: string };
type LinkTarget = RouteLinkTarget | ExternalLinkTarget;

export interface ProgramsTableSignature {
  Args: {
    results: ProgramsSearchResult;
    isLoading: boolean;

    // Column selection
    selectedColumns?: string[];
    availableFields?: FieldOption[];
    columnOrder?: string[];

    // Base route for constructing links (e.g., "authenticated.tuition-assistance" or "authenticated.admin.tas")
    baseRoute?: string;

    // Display options
    showIndividualApplications?: boolean;
    excludeNonVoucherPrograms?: boolean;
    courseCashProgramsOnly?: boolean;
    includeCourses?: boolean;
    includeInvoiceDate?: boolean;
    terminatedEmployees?: boolean;
    excludeTermVerified?: boolean;

    // Legacy routes for backward compatibility
    applicationDetailRoute?: string;
    programDetailRoute?: string;
    programTemplateDetailRoute?: string;
    peopleDetailRoute?: string;
    companyDetailRoute?: string;
    financialInstitutionDetailRoute?: string;
    planDetailRoute?: string;
    documentDetailRoute?: string;
    assetDetailRoute?: string;

    // Payment states to show
    paymentStates?: string;

    // Sort
    sortField?: string;
    onSort?: (sortField: string) => void;

    // Pagination
    limit: number;
    page: number;

    // Filter data for empty state
    selectedFilters?: Record<string, string | null>;

    // Callbacks
    onPageChange: (page: number) => void;
    onColumnOrderChange?: (order: string[]) => void; // ✅ NEW: Callback for column reorder events
  };
  Element: HTMLDivElement;
}

// ============================================================================
// CONSTANTS & CONFIGURATION
// ============================================================================

const DATE_FIELD_PATTERNS = [
  /_at$/, // ends with _at (created_at, updated_at, etc.)
  /_date$/, // ends with _date
  /^date_/, // starts with date_
  /_time$/, // ends with _time
  /timestamp/i, // contains timestamp
  /created/i, // contains created
  /updated/i, // contains updated
  /modified/i, // contains modified
  /deleted/i, // contains deleted
  /expired/i, // contains expired
] as const;

const CURRENCY_FIELD_PATTERNS = [
  /amount$/i, // ends with amount
  /amount_integer$/i, // ends with amount_integer (OpenSearch integer cents)
  /amountInteger$/i, // ends with AmountInteger (camelCase integer cents)
  /price$/i, // ends with price
  /cost$/i, // ends with cost
  /fee$/i, // ends with fee
  /tuition$/i, // ends with tuition
  /salary$/i, // ends with salary
  /wage$/i, // ends with wage
  /total$/i, // ends with total
  /balance$/i, // ends with balance
] as const;

// ============================================================================
// COLUMN MAPPING - Now imported from shared configuration
// ============================================================================
// Using TAS_PROGRAMS_COLUMN_MAPPING from tas-column-config.ts
// This ensures consistent column configuration across individual and aggregate tables
const COLUMN_MAPPING = TAS_PROGRAMS_COLUMN_MAPPING;

// ============================================================================
// STRING UTILITIES
// ============================================================================

/**
 * Auto-generates human-readable labels from field names.
 * Handles both camelCase and snake_case input, converts to Title Case.
 *
 * @future-extraction tio-common/utils/string-helpers
 */
// ============================================================================
// TYPE INFERENCE UTILITIES
// ============================================================================

/**
 * Infers column type based on field name patterns and sample value.
 * Used for dynamic column type detection when column mapping is not available.
 *
 * @future-extraction tio-common/utils/column-type-inference
 */
const inferColumnType = (fieldName: string, sampleValue?: unknown): ColumnType => {
  // Check field name patterns for dates
  const isDateField = DATE_FIELD_PATTERNS.some((pattern) => pattern.test(fieldName));

  // Check for currency patterns
  const isCurrencyField = CURRENCY_FIELD_PATTERNS.some((pattern) => pattern.test(fieldName));

  // If we have a sample value, check its format for runtime type detection
  if (sampleValue !== undefined && sampleValue !== null && sampleValue !== '') {
    const valueStr = String(sampleValue);

    // Check if the value is an array
    if (Array.isArray(sampleValue)) {
      // If empty array, default to text
      if (sampleValue.length === 0) {
        return 'text';
      }

      // Check the first element to determine array type
      const firstElement = sampleValue[0];

      // If first element is an object (but not null/date), it's array of objects
      if (
        typeof firstElement === 'object' &&
        firstElement !== null &&
        !(firstElement instanceof Date)
      ) {
        return 'array-objects';
      }

      return 'array-primitive';
    }

    // Check if value looks like a serialized array (starts with [ and contains objects)
    if (
      valueStr.startsWith('[') &&
      (valueStr.includes('[object Object]') || valueStr.includes('object Object'))
    ) {
      return 'array-objects';
    }

    // Check if value looks like a simple serialized array
    if (valueStr.startsWith('[') && valueStr.endsWith(']') && !valueStr.includes('object Object')) {
      return 'array-primitive';
    }

    // Check if value looks like an ISO date string
    const isoDatePattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
    const shortDatePattern = /^\d{4}-\d{2}-\d{2}$/;
    const timestampPattern = /^\d{10,13}$/; // Unix timestamp (10-13 digits)

    if (
      isoDatePattern.test(valueStr) ||
      shortDatePattern.test(valueStr) ||
      timestampPattern.test(valueStr)
    ) {
      return 'date';
    }

    // Check if value looks like currency (number or string with decimal)
    if (isCurrencyField && (typeof sampleValue === 'number' || /^\d+\.?\d*$/.test(valueStr))) {
      return 'currency';
    }
  }

  // Field name based inference
  if (isDateField) return 'date';
  if (isCurrencyField) return 'currency';

  return 'text';
};

// ============================================================================
// ARRAY FORMATTING UTILITIES
// ============================================================================

/**
 * Formats arrays of primitive values (strings, numbers, booleans) into
 * comma-separated strings.
 *
 * @future-extraction tio-common/utils/array-formatters
 */
const formatArrayPrimitive = (array: unknown) => {
  // Handle actual arrays
  if (Array.isArray(array)) {
    if (array.length === 0) return '';

    return array
      .filter((item) => item !== null && item !== undefined && item !== '')
      .map((item) => String(item))
      .join(', ');
  }

  // Handle serialized arrays or string representations
  const arrayStr = String(array);
  if (arrayStr.startsWith('[') && arrayStr.endsWith(']')) {
    // Try to parse as JSON first
    try {
      const parsed = JSON.parse(arrayStr);
      if (Array.isArray(parsed)) {
        return parsed
          .filter((item) => item !== null && item !== undefined && item !== '')
          .map((item) => String(item))
          .join(', ');
      }
    } catch {
      // If JSON parsing fails, treat as string and clean up
      return arrayStr
        .replace(/^\[|\]$/g, '') // Remove brackets
        .split(',')
        .map((item) => item.trim().replace(/^"|"$/g, '')) // Remove quotes
        .filter((item) => item && item !== '')
        .join(', ');
    }
  }

  return String(array);
};

/**
 * Formats arrays of objects with key-value display and separators.
 * Displays objects with labeled fields and separates multiple objects with dividers.
 *
 * @param array - The array of objects to format
 * @param config - Optional configuration for which fields to show
 * @future-extraction tio-common/utils/array-formatters
 */
const formatArrayObjects = (
  array: unknown,
  config?: ArrayObjectsConfig,
  keyConverter?: (key: string) => string
) => {
  // Handle actual arrays
  if (Array.isArray(array)) {
    if (array.length === 0) return '';

    return array
      .filter((item) => item !== null && item !== undefined)
      .map((item, index) => {
        if (typeof item !== 'object' || item === null) {
          return String(item);
        }

        const obj = item as Record<string, unknown>;

        // If config is provided, try to show only configured fields
        if (config?.fields && config.fields.length > 0) {
          const entries = config.fields
            .map((fieldConfig) => {
              const camelCaseField = keyConverter
                ? keyConverter(fieldConfig.field)
                : fieldConfig.field;
              const value = obj[camelCaseField];

              if (value === null || value === undefined || value === '') {
                return null; // Skip empty values
              }

              // Handle label: false = no label, undefined = auto-generate, string = use as-is
              const label =
                fieldConfig.label === false
                  ? false
                  : fieldConfig.label !== undefined
                    ? fieldConfig.label
                    : autoGenerateLabel(fieldConfig.field);

              // Format value based on type if specified
              let formattedValue = String(value);
              if (fieldConfig.type === 'currency' && typeof value === 'number') {
                formattedValue = `$${value.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}`;
              } else if (fieldConfig.type === 'date') {
                const date = new Date(value as string | number);
                if (!isNaN(date.getTime())) {
                  formattedValue = date.toLocaleDateString();
                }
              }

              // Only include label if it's not false
              return label !== false ? `${label}: ${formattedValue}` : formattedValue;
            })
            .filter((entry) => entry !== null);

          // If no configured fields were found, fall back to showing all fields
          // This ensures we don't hide data when field names don't match
          if (entries.length === 0) {
            // Fall through to default behavior below
          } else {
            const objectString = entries.join('\n');
            const separator = config.separator || '\n----\n';
            return index > 0 ? separator + objectString : objectString;
          }
        }

        // Default behavior: show all fields
        const entries = Object.entries(obj)
          .filter(([_, value]) => value !== null && value !== undefined && value !== '')
          .map(([key, value]) => {
            const label = autoGenerateLabel(key);

            // Format currency values if they look like money fields
            let formattedValue = String(value);
            if (
              typeof value === 'number' &&
              (key.toLowerCase().includes('tuition') ||
                key.toLowerCase().includes('total') ||
                key.toLowerCase().includes('amount') ||
                key.toLowerCase().includes('fee'))
            ) {
              formattedValue = `$${value.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}`;
            }

            return `${label}: ${formattedValue}`;
          });

        // Join key-value pairs with line breaks, and separate objects with divider
        const objectString = entries.join('\n');
        return index > 0 ? '----\n' + objectString : objectString;
      })
      .filter((str) => str !== null)
      .join('\n');
  }

  // Handle serialized arrays or string representations
  const arrayStr = String(array);

  // If it's clearly an array of objects that got stringified improperly
  if (arrayStr.includes('[object Object]') || arrayStr.includes('object Object')) {
    return 'Complex data (unable to parse object details)';
  }

  // Try to parse as JSON
  if (arrayStr.startsWith('[') && arrayStr.endsWith(']')) {
    try {
      const parsed = JSON.parse(arrayStr);
      if (Array.isArray(parsed)) {
        return formatArrayObjects(parsed, config, keyConverter); // Recursive call with parsed array
      }
    } catch {
      // If parsing fails, provide a fallback message
      return 'Complex data (parse error)';
    }
  }

  return String(array);
};

// ============================================================================
// COMPONENT CLASS
// ============================================================================

export default class ProgramsTableComponent extends Component<ProgramsTableSignature> {
  private readonly hiddenObjectColumnKeys = new Set([
    'employee_ef_fields.employee_custom_fields',
    'employee_ef_fields.employee_fields',
    'employeeEfFields.employeeCustomFields',
    'employeeEfFields.employeeFields',
  ]);

  /**
   * Cached lookup from snake_case path → camelCase resultKey.
   * Built from /fields API data so the frontend never does case conversion.
   */
  get resultKeyLookup(): Map<string, string> {
    return buildResultKeyLookup(this.args.availableFields);
  }

  /** Convert a snake_case field path to its camelCase result key using /fields data */
  private toResultKey(fieldPath: string): string {
    return getResultKey(fieldPath, this.resultKeyLookup);
  }

  // ============================================================================
  // CONFIGURATION
  // ============================================================================

  columnMapping = COLUMN_MAPPING;

  private asResultRecord(item: unknown): Record<string, unknown> {
    if (item && typeof item === 'object') {
      return item as Record<string, unknown>;
    }

    return {};
  }

  private isHiddenObjectColumn(columnKey: string): boolean {
    return this.hiddenObjectColumnKeys.has(columnKey);
  }

  private isStructuredCollectionColumnKey(columnKey: string): boolean {
    const normalized = columnKey.replace(/_/g, '').toLowerCase();
    return normalized.startsWith('tasapplicationcoursesobject');
  }

  private isStructuredDisplayValue(value: unknown): boolean {
    if (Array.isArray(value)) {
      return true;
    }

    return typeof value === 'object' && value !== null && !(value instanceof Date);
  }

  // ============================================================================
  // STATE FOR DRAG & DROP
  // ============================================================================

  draggedColumnIndex?: number;

  @tracked reorderedColumns: DisplayColumn[] | null = null;

  @tracked dropIndicatorPosition?: number;

  // ============================================================================
  // COMPUTED PROPERTIES
  // ============================================================================

  // Get the columns to display - either from selectedColumns prop or fallback to defaults
  get displayColumns(): DisplayColumn[] {
    let columns: DisplayColumn[] = [];

    if (this.args.selectedColumns && this.args.selectedColumns.length > 0) {
      const selectedColumns = this.args.selectedColumns.filter((columnKey) => {
        const normalizedKey = this.toResultKey(columnKey);
        return (
          !this.isHiddenObjectColumn(columnKey) &&
          !this.isHiddenObjectColumn(normalizedKey) &&
          !this.isStructuredCollectionColumnKey(columnKey) &&
          !this.isStructuredCollectionColumnKey(normalizedKey)
        );
      });

      columns = selectedColumns.map((columnKey): DisplayColumn => {
        // Normalize column key to camelCase for internal use
        // selectedColumns come in as snake_case from query params — look up the
        // camelCase resultKey from /fields so no frontend conversion is needed
        const normalizedKey = this.toResultKey(columnKey);
        const snakeKey = columnKey.replace(/([A-Z])/g, (letter) => `_${letter.toLowerCase()}`);
        const availableFieldLabel = findAvailableFieldLabel(
          this.args.availableFields,
          columnKey,
          normalizedKey
        );

        // Use hardcoded mapping if available (check against original snake_case key)
        const mappingKey =
          (snakeKey in this.columnMapping ? snakeKey : undefined) ||
          (columnKey in this.columnMapping ? columnKey : undefined);
        if (mappingKey) {
          const config = this.columnMapping[mappingKey as ColumnKey];
          return {
            key: normalizedKey, // Use camelCase key internally
            ...config,
            label: availableFieldLabel || config.label,
          };
        } else {
          // Get a sample value from the first item for type inference
          const sampleItem = this.args.results.items?.[0];
          const sampleValue = sampleItem ? this.getCellValue(sampleItem, normalizedKey) : undefined;

          // Use API-provided label, falling back to auto-generation
          const label =
            findAvailableFieldLabel(this.args.availableFields, normalizedKey, columnKey) ||
            autoGenerateLabel(normalizedKey);

          return {
            key: normalizedKey, // Use camelCase key internally
            label,
            type: inferColumnType(normalizedKey, sampleValue),
            linkable: false as const,
          };
        }
      });
    } else {
      // No selection provided - show all columns from the first result item
      // Get all keys from the first item in results
      const firstItem = this.args.results.items?.[0];
      if (firstItem) {
        const itemKeys = Object.keys(this.asResultRecord(firstItem)).filter((camelKey) => {
          if (this.isStructuredCollectionColumnKey(camelKey)) {
            return false;
          }

          const sampleValue = this.getCellValue(firstItem, camelKey);
          return !this.isStructuredDisplayValue(sampleValue);
        });

        columns = itemKeys.map((camelKey): DisplayColumn => {
          // Try to find mapping using the camelCase key
          // First check if there's a snake_case version in the mapping
          const snakeKey = camelKey.replace(/([A-Z])/g, (letter) => `_${letter.toLowerCase()}`);
          const mappingKey = (snakeKey in this.columnMapping ? snakeKey : camelKey) as ColumnKey;
          const availableFieldLabel = findAvailableFieldLabel(
            this.args.availableFields,
            snakeKey,
            camelKey
          );

          if (mappingKey in this.columnMapping) {
            const config = this.columnMapping[mappingKey];
            return {
              key: camelKey,
              ...config,
              label: availableFieldLabel || config.label,
            };
          } else {
            // Auto-generate config for unmapped columns
            const sampleValue = this.getCellValue(firstItem, camelKey);
            return {
              key: camelKey,
              label:
                findAvailableFieldLabel(this.args.availableFields, camelKey, snakeKey) ||
                autoGenerateLabel(camelKey),
              type: inferColumnType(camelKey, sampleValue),
              linkable: false as const,
            };
          }
        });
      }
    }

    if (this.args.columnOrder && this.args.columnOrder.length > 0) {
      columns = reorderTableColumns(
        columns as unknown as { key: string; [key: string]: unknown }[],
        this.args.columnOrder,
        (key: string) => this.toResultKey(key)
      ) as unknown as DisplayColumn[];
    }

    const visibleColumns = this.reorderedColumns || columns;

    // Labels are already disambiguated by the API's displayLabel
    return visibleColumns;
  }

  // ============================================================================
  // ROUTING METHODS
  // ============================================================================

  /**
   * Constructs full route from base route + sub route pattern.
   * Falls back to legacy routes if baseRoute is not provided.
   */
  buildRoute = (routePattern: string) => {
    if (routePattern.includes('financial-institutions')) {
      return this.args.financialInstitutionDetailRoute || routePattern;
    }
    if (routePattern.includes('companies')) {
      return this.args.companyDetailRoute || 'authenticated.companies.show';
    }
    if (routePattern.includes('people')) {
      return this.args.peopleDetailRoute || 'authenticated.people.show';
    }

    if (this.args.baseRoute) {
      const fullRoute = `${this.args.baseRoute}.${routePattern}`;
      return fullRoute;
    }

    // Fallback to legacy routes if no baseRoute provided
    if (routePattern.includes('applications')) {
      return (
        this.args.applicationDetailRoute || 'authenticated.tuition-assistance.applications.show'
      );
    }
    if (routePattern.includes('program-instances')) {
      return (
        this.args.programDetailRoute || 'authenticated.tuition-assistance.program-instances.show'
      );
    }

    return routePattern;
  };

  // ============================================================================
  // DATA RETRIEVAL METHODS
  // ============================================================================

  /**
   * Gets the appropriate model ID for linking based on column and item data.
   * Handles different column types (application_id, program instance, etc.)
   * Falls back to program instance ID if application ID is missing.
   */
  getModelId = (item: unknown, columnKey: string) => {
    const typedItem = this.asResultRecord(item);

    if (columnKey === 'id') {
      return (
        typedItem.tasApplicationIdKeyword ||
        typedItem.applicationId ||
        typedItem.tasProgramInstanceIdKeyword ||
        typedItem.programInstanceId ||
        typedItem.id ||
        ''
      );
    }

    // For application columns, look for application ID, fallback to program instance ID
    if (columnKey.includes('application')) {
      return (
        typedItem.tasApplicationIdKeyword ||
        typedItem.applicationId ||
        typedItem.tasProgramInstanceIdKeyword ||
        typedItem.programInstanceId ||
        typedItem.id ||
        ''
      );
    }

    // For program columns, look for program instance ID
    if (columnKey.includes('program')) {
      return (
        typedItem.tasProgramInstanceIdKeyword || typedItem.programInstanceId || typedItem.id || ''
      );
    }

    if (/company.*id/i.test(columnKey)) {
      return (
        typedItem.companyIdKeyword ||
        typedItem.companyId ||
        this.getCellValue(item, columnKey) ||
        ''
      );
    }

    if (/person/i.test(columnKey)) {
      const personId = this.getCellValue(item, columnKey);
      if (personId) {
        return personId;
      }
    }

    if (/financial.*institution.*id/i.test(columnKey)) {
      const institutionId = this.getCellValue(item, columnKey);
      if (institutionId) {
        return institutionId;
      }
    }

    // Default fallback
    return typedItem.id || '';
  };

  // ============================================================================
  // FORMATTING METHODS
  // ============================================================================

  /**
   * Formats cell value based on column type.
   * Handles currency, dates, arrays, and text with dynamic array detection.
   */
  formatCellValue = (value: unknown, type: ColumnType, arrayConfig?: ArrayObjectsConfig) => {
    // Check for empty values first
    const isEmpty = value === null || value === undefined || value === '';

    // For currency type, show "N/A" when no value
    if (isEmpty && type === 'currency') {
      return 'N/A';
    }

    if (isEmpty) return '';

    // 🚀 DYNAMIC ARRAY DETECTION - Override type if we detect an array
    if (Array.isArray(value)) {
      if (value.length === 0) {
        return '';
      }

      // Check first element to determine array type
      const firstElement = value[0];
      if (
        typeof firstElement === 'object' &&
        firstElement !== null &&
        !(firstElement instanceof Date)
      ) {
        return formatArrayObjects(value, arrayConfig, (key: string) => this.toResultKey(key));
      } else {
        return formatArrayPrimitive(value);
      }
    }

    switch (type) {
      case 'currency': {
        const numValue = typeof value === 'number' ? value : parseFloat(String(value));
        return isNaN(numValue)
          ? String(value)
          : `$${numValue.toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`;
      }
      case 'date': {
        // Simple date formatting for now - can be enhanced to use safeFormatDate helper in template
        if (typeof value === 'string' || typeof value === 'number') {
          const date = new Date(value);
          return isNaN(date.getTime()) ? String(value) : date.toLocaleDateString();
        }
        return String(value);
      }
      case 'boolean': {
        if (typeof value === 'boolean') {
          return value ? 'Yes' : 'No';
        }
        // Handle string representations of booleans
        const strValue = String(value).toLowerCase();
        if (strValue === 'true' || strValue === '1') return 'Yes';
        if (strValue === 'false' || strValue === '0') return 'No';
        return String(value);
      }
      case 'array-primitive':
        return formatArrayPrimitive(value);

      case 'array-objects':
        return formatArrayObjects(value, arrayConfig, (key: string) => this.toResultKey(key));

      case 'text':
      default:
        return String(value);
    }
  };

  /**
   * Gets value from item using column key.
   */
  getCellValue = (item: unknown, columnKey: string) => {
    const typedItem = this.asResultRecord(item);
    let value = typedItem[columnKey];

    if (columnKey === 'id') {
      const preferredId =
        typedItem.tasApplicationIdKeyword ||
        typedItem.applicationId ||
        typedItem.tasProgramInstanceIdKeyword ||
        typedItem.programInstanceId;
      if (preferredId !== undefined && preferredId !== null && preferredId !== '') {
        return preferredId;
      }
    }

    if (value === undefined && columnKey.includes('.')) {
      value = columnKey.split('.').reduce<unknown>((acc, part) => {
        if (!acc || typeof acc !== 'object') {
          return undefined;
        }

        return (acc as Record<string, unknown>)[part];
      }, typedItem);

      // Backward-compatibility for Employee EF custom fields:
      // some selected column keys point to employeeEfFields.<leaf>,
      // while values live under employeeEfFields.employeeCustomFields.<leaf>.
      // Treat empty-string placeholders as missing so fallback lookup still runs.
      if (
        (value === undefined || value === null || value === '') &&
        columnKey.startsWith('employeeEfFields.') &&
        !columnKey.startsWith('employeeEfFields.employeeCustomFields.')
      ) {
        const leaf = columnKey.split('.').filter(Boolean).at(-1);
        if (leaf) {
          const employeeEf =
            (typedItem.employeeEfFields as Record<string, unknown> | undefined) ||
            (typedItem['employee_ef_fields'] as Record<string, unknown> | undefined);

          // Some Employee/Custom fields are indexed directly under employeeEfFields
          // (camelCase or snake_case), not only inside employeeCustomFields.
          const directEfValue = employeeCustomFieldValue(employeeEf, leaf);
          if (directEfValue !== undefined) {
            value = directEfValue;
          }

          const customValue =
            (employeeEf?.employeeCustomFields as Record<string, unknown> | undefined) ||
            (employeeEf?.employee_custom_fields as Record<string, unknown> | undefined);
          if (value === undefined || value === null || value === '') {
            value = employeeCustomFieldValue(customValue, leaf);
          }
        }
      }

      // Course object fields are stored as a nested array (one entry per course).
      // Collect and comma-join the leaf value across all courses.
      if (
        (value === undefined || value === null || value === '') &&
        columnKey.startsWith('tasApplicationCoursesObject.')
      ) {
        const courses = typedItem['tasApplicationCoursesObject'];
        if (Array.isArray(courses)) {
          const leafKey = columnKey.slice('tasApplicationCoursesObject.'.length);
          const values = (courses as Array<Record<string, unknown>>)
            .map((course) => course[leafKey])
            .filter((v) => v !== undefined && v !== null && v !== '');
          if (values.length > 0) {
            value = values.join(', ');
          }
        }
      }

      // TAS custom fields can appear under legacy nested keys or snake_case keys
      // inside the flattened container object. If the direct lookup misses,
      // perform a normalized key match within that container.
      if (value === undefined || value === null || value === '') {
        const customValue = tasCustomFieldValue(typedItem, columnKey);
        if (customValue !== undefined) {
          value = customValue;
        }
      }
    }

    // Auto-convert payment amounts from cents to dollars
    // If the field name contains "payment" and ends with "amount", divide by 100
    if (
      typeof value === 'number' &&
      columnKey.toLowerCase().includes('payment') &&
      columnKey.toLowerCase().includes('amount')
    ) {
      value = value / 100;
    }

    return value !== undefined && value !== null ? value : '';
  };

  // ============================================================================
  // VALIDATION & CHECK METHODS
  // ============================================================================

  /**
   * Type guard to check if a column is linkable (has a route pattern).
   */
  isLinkableColumn = (linkable: string | false): linkable is string => {
    return typeof linkable === 'string' && linkable !== '';
  };

  private isExternalUrlValue(value: unknown): value is string {
    return typeof value === 'string' && /^https?:\/\//i.test(value);
  }

  private isExternalUrlColumn(columnKey: string): boolean {
    return /(file|document|asset|attachment|upload|download|url)/i.test(columnKey);
  }

  isExternalLinkTarget(linkTarget: LinkTarget): linkTarget is ExternalLinkTarget {
    return 'href' in linkTarget;
  }

  /**
   * Some saved report configurations include generic `id` instead of explicit
   * TAS id fields. When TAS identifiers are present, we can still resolve a
   * detail route and keep the id cell clickable.
   */
  private isDynamicTasIdColumn = (column: DisplayColumn, item: unknown): boolean => {
    if (column.key !== 'id') return false;

    const typedItem = this.asResultRecord(item);
    return Boolean(
      typedItem.tasApplicationIdKeyword ||
      typedItem.applicationId ||
      typedItem.tasProgramInstanceIdKeyword ||
      typedItem.programInstanceId
    );
  };

  private getDynamicTasIdLink = (item: unknown): RouteLinkTarget | null => {
    const typedItem = this.asResultRecord(item);

    const explicitApplicationId = typedItem.tasApplicationIdKeyword || typedItem.applicationId;
    if (explicitApplicationId) {
      return {
        route: this.buildRoute('applications.show'),
        modelId: String(explicitApplicationId),
      };
    }

    const explicitProgramInstanceId =
      typedItem.tasProgramInstanceIdKeyword || typedItem.programInstanceId;
    if (explicitProgramInstanceId) {
      return {
        route: this.buildRoute('program-instances.show'),
        modelId: String(explicitProgramInstanceId),
      };
    }

    return null;
  };

  private getFallbackLinkTarget(item: unknown, columnKey: string): RouteLinkTarget | null {
    const normalizedKey = columnKey.toLowerCase();
    const cellValue = this.getCellValue(item, columnKey);
    if (!cellValue) return null;

    if (this.args.peopleDetailRoute && /person.*id/.test(normalizedKey)) {
      return { route: this.args.peopleDetailRoute, modelId: String(cellValue) };
    }

    if (this.args.planDetailRoute && /plan.*id/.test(normalizedKey)) {
      return { route: this.args.planDetailRoute, modelId: String(cellValue) };
    }

    if (this.args.programTemplateDetailRoute && /programtemplate.*id/.test(normalizedKey)) {
      return { route: this.args.programTemplateDetailRoute, modelId: String(cellValue) };
    }

    if (this.args.programDetailRoute && /programinstance.*id/.test(normalizedKey)) {
      return { route: this.args.programDetailRoute, modelId: String(cellValue) };
    }

    if (this.args.documentDetailRoute && /document.*id/.test(normalizedKey)) {
      return { route: this.args.documentDetailRoute, modelId: String(cellValue) };
    }

    if (this.args.assetDetailRoute && /asset.*id/.test(normalizedKey)) {
      return { route: this.args.assetDetailRoute, modelId: String(cellValue) };
    }

    if (
      this.args.financialInstitutionDetailRoute &&
      /financial.*institution.*id/.test(normalizedKey)
    ) {
      return { route: this.args.financialInstitutionDetailRoute, modelId: String(cellValue) };
    }

    return null;
  }

  getLinkTarget = (item: unknown, column: DisplayColumn): LinkTarget | null => {
    const cellValue = this.getCellValue(item, column.key);
    if (this.isExternalUrlColumn(column.key) && this.isExternalUrlValue(cellValue)) {
      return { href: cellValue };
    }

    if (this.isLinkableColumn(column.linkable)) {
      if (
        column.linkable.includes('financial-institutions') &&
        !this.args.financialInstitutionDetailRoute
      ) {
        return null;
      }
      if (column.linkable.includes('companies') && !this.args.companyDetailRoute) {
        return null;
      }
      const modelId = this.getModelId(item, column.key);
      if (!modelId) return null;

      return {
        route: this.buildRoute(column.linkable),
        modelId: String(modelId),
      };
    }

    if (this.isDynamicTasIdColumn(column, item)) {
      return this.getDynamicTasIdLink(item);
    }

    return this.getFallbackLinkTarget(item, column.key);
  };

  /**
   * Checks if content contains newlines (indicating formatted array content).
   */
  isMultilineContent = (content: string) => {
    return typeof content === 'string' && content.includes('\n');
  };

  /**
   * Checks if a given state exists in the paymentStates filter.
   */
  containsPaymentState = (state: string) => {
    return this.args.paymentStates?.split(',').includes(state);
  };

  // ============================================================================
  // DRAG & DROP HANDLERS
  // ============================================================================

  /**
   * Handle drag start on column header
   */
  onDragStart = (index: number, event: DragEvent) => {
    this.draggedColumnIndex = index;
    this.dropIndicatorPosition = undefined;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', String(index));
    }
  };

  /**
   * Handle drag over column header - show drop indicator
   */
  onDragOver = (index: number, event: DragEvent) => {
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
    // Show drop indicator at this position
    this.dropIndicatorPosition = index;
  };

  /**
   * Handle drop on column header - reorder columns and log to console
   */
  onDrop = (dropIndex: number, event: DragEvent) => {
    event.preventDefault();
    this.dropIndicatorPosition = undefined;

    if (this.draggedColumnIndex === undefined || this.draggedColumnIndex === dropIndex) {
      this.draggedColumnIndex = undefined;
      return;
    }

    // Create new column order by moving dragged column to drop position
    const newColumns = [...this.displayColumns];
    const draggedColumnData = newColumns[this.draggedColumnIndex];

    if (!draggedColumnData) {
      this.draggedColumnIndex = undefined;
      return;
    }

    newColumns.splice(this.draggedColumnIndex, 1);
    newColumns.splice(dropIndex, 0, draggedColumnData);

    // Store the reordered columns for display
    this.reorderedColumns = newColumns;

    // Convert back to snake_case column keys for storage/API
    const newOrder = newColumns.map((col) => {
      const snakeKey = col.key.replace(/([A-Z])/g, (letter) => `_${letter.toLowerCase()}`);
      return snakeKey;
    });

    // Log the new order for testing
    console.log('Column order changed:', newOrder);
    console.log(
      'Column objects:',
      newColumns.map((c) => ({ key: c.key, label: c.label }))
    );

    // Call the callback if provided
    if (this.args.onColumnOrderChange) {
      this.args.onColumnOrderChange(newOrder);
    }

    this.draggedColumnIndex = undefined;
  };

  /**
   * Determine whether a column can be sorted in OpenSearch.
   * Only keyword, date, integer/long/float/short/byte, and boolean typed fields
   * are sortable.  Text fields cause 400/422 errors unless they have a .keyword
   * sub-field (which would appear as a separate column).
   *
   * Uses the camelCase column key suffixes to infer sortability.
   */
  isSortableColumn = (key: string): boolean => {
    const sortableSuffixes = [
      'Keyword',
      'KeywordAnalyzable',
      'Date',
      'Integer',
      'Long',
      'Float',
      'Short',
      'Byte',
      'Boolean',
    ];
    return sortableSuffixes.some((suffix) => key.endsWith(suffix));
  };

  <template>
    <div ...attributes>
      <Table @isLoading={{@isLoading}} @surface={{true}}>
        <:thead as |options|>
          <options.tr>
            {{#each this.displayColumns as |column index|}}
              <options.th
                @sortField={{if (this.isSortableColumn column.key) column.key}}
                @activeSortField={{@sortField}}
                @onSort={{@onSort}}
                @sortFormat="suffix"
                draggable="true"
                {{on "dragstart" (fn this.onDragStart index)}}
                {{on "dragover" (fn this.onDragOver index)}}
                {{on "drop" (fn this.onDrop index)}}
                class="cursor-move select-none relative
                  {{if (eq this.draggedColumnIndex index) 'opacity-50'}}"
              >
                {{column.label}}
                {{#if (eq this.dropIndicatorPosition index)}}
                  <div
                    class="absolute left-0 top-0 bottom-0 w-1 bg-blue-500 pointer-events-none"
                  ></div>
                {{/if}}
              </options.th>
            {{/each}}
          </options.tr>
        </:thead>
        <:tbody as |options|>
          {{! @glint-expect-error: search results can have different types }}
          {{#each @results.items as |item|}}
            <options.tr class="align-top">
              {{#each this.displayColumns as |column|}}
                <options.td>
                  {{#let (this.getLinkTarget item column) as |linkTarget|}}
                    {{#if linkTarget}}
                      {{#if (this.isExternalLinkTarget linkTarget)}}
                        <a
                          href={{linkTarget.href}}
                          target="_blank"
                          rel="noopener noreferrer"
                          class="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline"
                        >
                          {{#let
                            (this.formatCellValue
                              (this.getCellValue item column.key) column.type column.arrayConfig
                            )
                            as |formattedValue|
                          }}
                            {{#if (this.isMultilineContent formattedValue)}}
                              <div class="whitespace-pre-line">{{formattedValue}}</div>
                            {{else}}
                              {{formattedValue}}
                            {{/if}}
                          {{/let}}
                        </a>
                      {{else}}
                        <LinkTo
                          @route={{linkTarget.route}}
                          @model={{linkTarget.modelId}}
                          class="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline"
                        >
                          {{#let
                            (this.formatCellValue
                              (this.getCellValue item column.key) column.type column.arrayConfig
                            )
                            as |formattedValue|
                          }}
                            {{#if (this.isMultilineContent formattedValue)}}
                              <div class="whitespace-pre-line">{{formattedValue}}</div>
                            {{else}}
                              {{formattedValue}}
                            {{/if}}
                          {{/let}}
                        </LinkTo>
                      {{/if}}
                    {{else}}
                      {{#let
                        (this.formatCellValue
                          (this.getCellValue item column.key) column.type column.arrayConfig
                        )
                        as |formattedValue|
                      }}
                        {{#if (this.isMultilineContent formattedValue)}}
                          <div class="whitespace-pre-line">{{formattedValue}}</div>
                        {{else}}
                          {{formattedValue}}
                        {{/if}}
                      {{/let}}
                    {{/if}}
                  {{/let}}
                </options.td>
              {{/each}}
            </options.tr>
          {{else}}
            <options.tr>
              <options.td colspan="{{this.displayColumns.length}}">
                <div class="text-sm text-gray-700 dark:text-gray-300">
                  <p>You have searched for applications reports with the following criteria and
                    found no results:</p>
                  {{#if @selectedFilters}}
                    <ul class="list-disc pl-5 flex flex-col gap-y-1">
                      {{#each-in @selectedFilters as |key value|}}
                        {{#if value}}
                          <li>{{key}}: {{value}}</li>
                        {{/if}}
                      {{/each-in}}
                    </ul>
                  {{/if}}
                </div>
              </options.td>
            </options.tr>
          {{/each}}
        </:tbody>
        <:tfoot as |options|>
          <options.tr>
            <td colspan="15">
              <Paginator
                @totalItems={{@results.total}}
                @pageSize={{@limit}}
                @currentPage={{@page}}
                @onChange={{@onPageChange}}
              />
            </td>
          </options.tr>
        </:tfoot>
      </Table>
    </div>
  </template>
}
